import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  BookOpenCheck,
  CheckCircle2,
  CloudDownload,
  CloudUpload,
  Database,
  Download,
  ExternalLink,
  FileJson,
  FileUp,
  FlaskConical,
  Globe2,
  Layers3,
  Loader2,
  Play,
  Plus,
  RefreshCw,
  Save,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Trash2,
} from 'lucide-react';
import {
  aiGuidePackageFilename,
  CANONICAL_VISITOR_TOPICS,
  createAiGuidePackage,
  createAiGuideRecord,
  mergeAiGuideResearchDraft,
  normalizeAiGuidePackage,
  syncAiGuideShadowRecordedText,
  validateAiGuidePackage,
} from './aiGuideContentSchema';
import {
  EVIDENCE_ROLE_KEYS,
  EXPERIENCE_CONTRACT_VERSION,
  opportunityReviewInputHash,
  recordedTextHash,
  sha256Text,
  updateShadowRecordedText,
} from './aiGuideExperienceContract';
import { visitorLanguageReviewInput } from './aiGuideQuality';
import { aiGuideContentService } from '../../services/aiGuideContentService';
import { aiGuidePreviewService } from '../../services/aiGuidePreviewService';

const SECTIONS = [
  { id: 'overview', label: 'Package setup', icon: FileJson },
  { id: 'research', label: 'Automatic research', icon: Globe2 },
  { id: 'sources', label: 'Sources', icon: BookOpenCheck, countKey: 'sources' },
  { id: 'claims', label: 'Claims', icon: Database, countKey: 'claims' },
  { id: 'story-map', label: 'Story Map', icon: Sparkles },
  { id: 'shadow', label: 'Guide Shadow', icon: ScrollText, countKey: 'guide_shadow' },
  { id: 'governance', label: 'Governance', icon: ShieldCheck },
  { id: 'preview', label: 'Real AI preview', icon: FlaskConical },
  { id: 'validation', label: 'Validate & export', icon: CheckCircle2 },
];

const STORY_MAP_COLLECTIONS = [
  { id: 'topics', label: 'Internal topics', idKey: 'topic_id', titleKey: 'label' },
  {
    id: 'story_opportunities',
    label: 'Story opportunities',
    idKey: 'opportunity_id',
    titleKey: 'discovery_title',
  },
  {
    id: 'story_promises',
    label: 'Story promises',
    idKey: 'promise_id',
    titleKey: 'label',
  },
  {
    id: 'visitor_topic_prompts',
    label: 'Visitor choices',
    idKey: 'topic_prompt_id',
    titleKey: 'title',
  },
];

const text = (value) => String(value ?? '');
const toList = (value) => (
  text(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
);

const inputClass = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100';

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  );
}

function TextField({ label, value, onChange, hint, type = 'text', placeholder = '' }) {
  return (
    <Field label={label} hint={hint}>
      <input
        type={type}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={inputClass}
      />
    </Field>
  );
}

function TextAreaField({
  label,
  value,
  onChange,
  hint,
  rows = 4,
  placeholder = '',
  maxLength,
}) {
  return (
    <Field label={label} hint={hint}>
      <textarea
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        rows={rows}
        placeholder={placeholder}
        maxLength={maxLength}
        className={`${inputClass} resize-y`}
      />
    </Field>
  );
}

function CsvField({ label, value, onChange, hint, placeholder = 'id_one, id_two' }) {
  return (
    <TextField
      label={label}
      value={(Array.isArray(value) ? value : []).join(', ')}
      onChange={(next) => onChange(toList(next))}
      hint={hint}
      placeholder={placeholder}
    />
  );
}

function SelectField({ label, value, onChange, options, hint }) {
  return (
    <Field label={label} hint={hint}>
      <select
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        className={inputClass}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

function JsonObjectField({ label, value, onChange, hint }) {
  const serialized = useMemo(() => JSON.stringify(value || {}, null, 2), [value]);
  const [draft, setDraft] = useState(serialized);
  const [error, setError] = useState('');

  useEffect(() => {
    setDraft(serialized);
    setError('');
  }, [serialized]);

  const commit = () => {
    try {
      const parsed = JSON.parse(draft || '{}');
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Enter a JSON object.');
      }
      onChange(parsed);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Field label={label} hint={hint}>
      <textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        rows={5}
        spellCheck={false}
        className={`${inputClass} resize-y font-mono text-xs`}
      />
      {error && <span className="mt-1 block text-xs font-medium text-red-600">{error}</span>}
    </Field>
  );
}

function CanonicalTopicPicker({ selected, onChange }) {
  const selectedIds = new Set(Array.isArray(selected) ? selected : []);

  const toggle = (topicId) => {
    if (selectedIds.has(topicId)) {
      onChange([...selectedIds].filter((id) => id !== topicId));
    } else {
      onChange([...selectedIds, topicId]);
    }
  };

  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {CANONICAL_VISITOR_TOPICS.map((topic) => {
        const active = selectedIds.has(topic.id);
        return (
          <button
            key={topic.id}
            type="button"
            onClick={() => toggle(topic.id)}
            className={`rounded-lg border px-3 py-2 text-left transition ${
              active
                ? 'border-sky-500 bg-sky-50 text-sky-900'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
            }`}
          >
            <span className="block text-sm font-medium">{topic.label}</span>
            <span className="block font-mono text-[11px] text-slate-400">{topic.id}</span>
          </button>
        );
      })}
    </div>
  );
}

function Stat({ label, value, tone = 'slate' }) {
  const tones = {
    slate: 'border-slate-200 bg-white text-slate-900',
    blue: 'border-sky-200 bg-sky-50 text-sky-900',
    green: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="mt-1 text-xs font-medium uppercase tracking-wide opacity-70">{label}</div>
    </div>
  );
}

function EmptyCollection({ label, onAdd }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center">
      <p className="text-sm text-slate-500">No {label.toLowerCase()} yet.</p>
      <button
        type="button"
        onClick={onAdd}
        className="mt-4 inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
      >
        <Plus className="h-4 w-4" />
        Add first
      </button>
    </div>
  );
}

function CollectionWorkspace({
  items,
  selectedIndex,
  onSelect,
  onAdd,
  onDelete,
  titleFor,
  idFor,
  children,
  emptyLabel,
}) {
  if (items.length === 0) {
    return <EmptyCollection label={emptyLabel} onAdd={onAdd} />;
  }

  const safeIndex = Math.min(selectedIndex, items.length - 1);

  return (
    <div className="grid min-h-[34rem] gap-5 lg:grid-cols-[17rem_minmax(0,1fr)]">
      <aside className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <button
          type="button"
          onClick={onAdd}
          className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
        <div className="max-h-[31rem] space-y-1 overflow-auto pr-1">
          {items.map((item, index) => (
            <button
              key={`${idFor(item) || 'record'}-${index}`}
              type="button"
              onClick={() => onSelect(index)}
              className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                safeIndex === index
                  ? 'border-sky-300 bg-white shadow-sm'
                  : 'border-transparent text-slate-600 hover:bg-white'
              }`}
            >
              <span className="block truncate text-sm font-medium">
                {titleFor(item) || `Untitled ${index + 1}`}
              </span>
              <span className="block truncate font-mono text-[10px] text-slate-400">
                {idFor(item) || 'missing id'}
              </span>
            </button>
          ))}
        </div>
      </aside>
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-5 flex items-center justify-between border-b border-slate-100 pb-4">
          <div>
            <p className="text-sm font-semibold text-slate-900">
              {titleFor(items[safeIndex]) || `Untitled ${safeIndex + 1}`}
            </p>
            <p className="font-mono text-xs text-slate-400">{idFor(items[safeIndex])}</p>
          </div>
          <button
            type="button"
            onClick={() => onDelete(safeIndex)}
            className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
        {children(items[safeIndex], safeIndex)}
      </div>
    </div>
  );
}

function SourceEditor({ item, onChange }) {
  const set = (key, value) => onChange({ ...item, [key]: value });
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <TextField label="Source ID" value={item.source_id} onChange={(v) => set('source_id', v)} />
      <TextField label="Title" value={item.title} onChange={(v) => set('title', v)} />
      <TextField
        label="Source type"
        value={item.source_type}
        onChange={(v) => set('source_type', v)}
        placeholder="archive, partner script, publication..."
      />
      <TextField
        label="Reference"
        value={item.reference}
        onChange={(v) => set('reference', v)}
        placeholder="URL, filename or archive reference"
      />
      <TextField label="Authority" value={item.authority} onChange={(v) => set('authority', v)} />
      <SelectField
        label="Review status"
        value={item.review_status || 'draft'}
        onChange={(v) => set('review_status', v)}
        options={[
          { value: 'draft', label: 'Draft' },
          { value: 'reviewed', label: 'Reviewed' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' },
        ]}
      />
      <div className="md:col-span-2">
        <TextField
          label="Rights / reuse"
          value={item.rights}
          onChange={(v) => set('rights', v)}
        />
      </div>
    </div>
  );
}

function ClaimEditor({ item, onChange }) {
  const set = (key, value) => onChange({ ...item, [key]: value });
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <TextField label="Claim ID" value={item.claim_id} onChange={(v) => set('claim_id', v)} />
        <SelectField
          label="Claim type"
          value={item.claim_type || 'fact'}
          onChange={(v) => set('claim_type', v)}
          options={[
            { value: 'fact', label: 'Fact' },
            { value: 'historical_fact', label: 'Historical fact' },
            { value: 'interpretation', label: 'Interpretation' },
            { value: 'anecdote', label: 'Anecdote' },
            { value: 'contested', label: 'Contested' },
          ]}
        />
      </div>
      <TextAreaField
        label="Atomic assertion"
        value={item.text}
        onChange={(v) => set('text', v)}
        rows={3}
        hint="One independently reviewable assertion. Split bundled facts into separate claims."
      />
      <div className="grid gap-4 md:grid-cols-3">
        <CsvField
          label="Source refs"
          value={item.source_refs}
          onChange={(v) => set('source_refs', v)}
        />
        <CsvField
          label="Topic IDs"
          value={item.topic_ids}
          onChange={(v) => set('topic_ids', v)}
        />
        <CsvField
          label="Stop IDs"
          value={item.stop_ids}
          onChange={(v) => set('stop_ids', v)}
          hint="Optional. Territory-wide claims may have no stop."
        />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          label="Source locator"
          value={item.source_locator}
          onChange={(v) => set('source_locator', v)}
          hint="Page, section, timestamp, archive shelfmark, or another precise locator."
        />
        <TextAreaField
          label="Evidence excerpt"
          value={item.source_excerpt}
          onChange={(v) => set('source_excerpt', v)}
          rows={2}
          hint="Exact support retained for editorial review, not routine narration."
        />
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <TextField
          label="Confidence"
          value={item.confidence}
          onChange={(v) => set('confidence', v)}
        />
        <SelectField
          label="Review status"
          value={item.review_status || 'draft'}
          onChange={(v) => set('review_status', v)}
          options={[
            { value: 'draft', label: 'Draft' },
            { value: 'researched', label: 'Researched' },
            { value: 'approved', label: 'Approved' },
            { value: 'rejected', label: 'Rejected' },
          ]}
        />
        <Field
          label="Official policy"
          hint="Only approved evidence should be enabled for official mode."
        >
          <button
            type="button"
            onClick={() => set('official_safe', item.official_safe !== true)}
            className={`w-full rounded-lg border px-3 py-2 text-sm font-medium ${
              item.official_safe === true
                ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                : 'border-slate-300 bg-slate-50 text-slate-600'
            }`}
          >
            {item.official_safe === true ? 'Official safe' : 'Not approved for official'}
          </button>
        </Field>
      </div>
      <TextAreaField
        label="Why it matters"
        value={item.why_it_matters}
        onChange={(v) => set('why_it_matters', v)}
        rows={2}
        hint="Narrative value, not another factual assertion."
      />
    </div>
  );
}

function TopicEditor({ item, onChange }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <TextField
        label="Topic ID"
        value={item.topic_id}
        onChange={(value) => onChange({ ...item, topic_id: value })}
      />
      <TextField
        label="Editorial label"
        value={item.label}
        onChange={(value) => onChange({ ...item, label: value })}
        hint="Internal retrieval language, not the visitor-facing choice."
      />
    </div>
  );
}

function OpportunityEditor({ item, onChange, packageData }) {
  const set = (key, value) => onChange({ ...item, [key]: value });
  const setReviewedInput = (key, value) => onChange({
    ...item,
    [key]: value,
    experience_review: {
      ...(item.experience_review || {}),
      status: 'draft',
      reviewed_input_hash: '',
      reviewed_at: '',
    },
  });
  const currentReviewHash = opportunityReviewInputHash(
    item,
    packageData?.guide_shadow,
  );
  const experienceReviewIsCurrent = item.experience_review?.status === 'reviewed'
    && item.experience_review?.reviewed_input_hash === currentReviewHash
    && Boolean(item.experience_review?.reviewed_at);
  const connectedRecordings = (item.anchor_stop_ids || []).map((stopId) => ({
    stopId,
    shadow: (packageData?.guide_shadow || []).find((shadow) => shadow.stop_id === stopId),
  }));

  const markExperienceReviewed = () => onChange({
    ...item,
    experience_review: {
      status: 'reviewed',
      reviewed_input_hash: currentReviewHash,
      reviewed_at: new Date().toISOString(),
    },
  });
  const updateLens = (index, key, value) => setReviewedInput(
    'visitor_lenses',
    (item.visitor_lenses || []).map((lens, lensIndex) => (
      lensIndex === index ? { ...lens, [key]: value } : lens
    )),
  );
  const addLens = () => setReviewedInput('visitor_lenses', [
    ...(item.visitor_lenses || []),
    {
      lens_id: `lens_${Date.now().toString(36)}`,
      canonical_topic_weights: {},
      central_question: '',
      experience_contribution: '',
      claim_refs: [],
      depth: 'standard',
      fit_reason: '',
    },
  ]);
  const removeLens = (index) => setReviewedInput(
    'visitor_lenses',
    (item.visitor_lenses || []).filter((_, lensIndex) => lensIndex !== index),
  );

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          label="Opportunity ID"
          value={item.opportunity_id}
          onChange={(v) => set('opportunity_id', v)}
        />
        <TextField
          label="Discovery title"
          value={item.discovery_title}
          onChange={(v) => set('discovery_title', v)}
          hint="Concrete and attractive, but do not reveal the whole story."
        />
      </div>
      <TextAreaField label="Teaser" value={item.teaser} onChange={(v) => set('teaser', v)} rows={2} />
      <TextAreaField
        label="Central question"
        value={item.central_question}
        onChange={(v) => setReviewedInput('central_question', v)}
        rows={2}
      />
      <div className="rounded-xl border border-violet-200 bg-violet-50 p-4">
        <TextAreaField
          label="Intended new visitor understanding"
          value={item.experience_contribution}
          onChange={(v) => setReviewedInput('experience_contribution', v)}
          rows={3}
          maxLength={800}
          placeholder="After the connected recording(s), the visitor will additionally understand..."
          hint={`${text(item.experience_contribution).length}/800 characters. Describe the added understanding, not a writing style or another teaser.`}
        />
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <span className={`text-xs font-semibold ${
            experienceReviewIsCurrent ? 'text-emerald-700' : 'text-amber-700'
          }`}>
            {experienceReviewIsCurrent
              ? `Reviewed ${item.experience_review.reviewed_at}`
              : 'Draft or stale: review again after changing the question, evidence, or connected recording coverage.'}
          </span>
          <button
            type="button"
            onClick={markExperienceReviewed}
            disabled={!text(item.experience_contribution).trim()}
            className="rounded-lg border border-violet-300 bg-white px-3 py-2 text-xs font-semibold text-violet-800 hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Mark contribution reviewed
          </button>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <CsvField
          label="Claim refs"
          value={item.claim_refs}
          onChange={(v) => setReviewedInput('claim_refs', v)}
        />
        <CsvField
          label="Anchor stop IDs"
          value={item.anchor_stop_ids}
          onChange={(v) => setReviewedInput('anchor_stop_ids', v)}
          hint="Optional. New AI stops may live beyond recorded stops."
        />
        <CsvField
          label="Internal topic IDs"
          value={item.topic_ids}
          onChange={(v) => set('topic_ids', v)}
        />
      </div>
      <div className="rounded-xl border border-sky-200 bg-sky-50 p-4">
        <SelectField
          label="Personalization approach"
          value={item.personalization_mode || ''}
          onChange={(value) => setReviewedInput('personalization_mode', value)}
          options={[
            { value: '', label: 'Not decided (legacy)' },
            { value: 'universal', label: 'Universal explanation' },
            { value: 'lenses', label: 'Reviewed visitor lenses' },
          ]}
        />
        <p className="mt-2 text-xs leading-5 text-sky-800">
          Universal means the same question and evidence genuinely suit different interests. Lenses
          change the question, evidence, and depth when that creates a better explanation.
        </p>
        {item.personalization_mode === 'lenses' && (
          <div className="mt-4 space-y-4">
            {(item.visitor_lenses || []).map((lens, index) => (
              <div key={lens.lens_id || index} className="rounded-xl border border-sky-200 bg-white p-4">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <h4 className="text-sm font-semibold text-slate-900">Visitor lens {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => removeLens(index)}
                    className="rounded-lg p-2 text-red-500 hover:bg-red-50"
                    title="Remove visitor lens"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <TextField
                    label="Lens ID"
                    value={lens.lens_id}
                    onChange={(value) => updateLens(index, 'lens_id', value)}
                  />
                  <SelectField
                    label="Depth"
                    value={lens.depth || 'standard'}
                    onChange={(value) => updateLens(index, 'depth', value)}
                    options={[
                      { value: 'brief', label: 'Brief' },
                      { value: 'standard', label: 'Standard' },
                      { value: 'deep', label: 'Deep' },
                    ]}
                  />
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <JsonObjectField
                    label="Canonical topic weights"
                    value={lens.canonical_topic_weights}
                    onChange={(value) => updateLens(index, 'canonical_topic_weights', value)}
                    hint={'Example: {"science_technology": 1, "history_heritage": 0.4}'}
                  />
                  <CsvField
                    label="Lens claim refs"
                    value={lens.claim_refs}
                    onChange={(value) => updateLens(index, 'claim_refs', value)}
                    hint="Every claim must also belong to the parent opportunity."
                  />
                </div>
                <div className="mt-3 space-y-3">
                  <TextAreaField
                    label="Lens central question"
                    value={lens.central_question}
                    onChange={(value) => updateLens(index, 'central_question', value)}
                    rows={2}
                  />
                  <TextAreaField
                    label="Lens intended understanding"
                    value={lens.experience_contribution}
                    onChange={(value) => updateLens(index, 'experience_contribution', value)}
                    rows={2}
                    maxLength={800}
                  />
                  <TextAreaField
                    label="Why this lens improves the experience"
                    value={lens.fit_reason}
                    onChange={(value) => updateLens(index, 'fit_reason', value)}
                    rows={2}
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addLens}
              className="inline-flex items-center gap-2 rounded-lg border border-sky-300 bg-white px-3 py-2 text-sm font-semibold text-sky-900 hover:bg-sky-100"
            >
              <Plus className="h-4 w-4" />
              Add visitor lens
            </button>
          </div>
        )}
      </div>
      <Field
        label="Evidence roles"
        hint="Assign only roles the reviewed evidence can genuinely support. Empty roles are allowed."
      >
        <div className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-2">
          {EVIDENCE_ROLE_KEYS.map((role) => (
            <CsvField
              key={role}
              label={{
                hook: 'Hook claim IDs',
                explanation: 'Explanation claim IDs',
                human_connection: 'Human connection claim IDs',
                consequence: 'Consequence claim IDs',
                observable_cue: 'Observable cue claim IDs',
              }[role]}
              value={item.evidence_roles?.[role] || []}
              onChange={(value) => setReviewedInput('evidence_roles', {
                ...(item.evidence_roles || {}),
                [role]: value,
              })}
            />
          ))}
        </div>
      </Field>
      <div className="grid gap-4 md:grid-cols-2">
        <JsonObjectField
          label="Canonical topic weights"
          value={item.canonical_topic_weights}
          onChange={(v) => set('canonical_topic_weights', v)}
          hint={'Example: {"architecture_urbanism": 0.9, "history_heritage": 0.6}'}
        />
        <JsonObjectField
          label="Narrative motif weights"
          value={item.motif_weights}
          onChange={(v) => set('motif_weights', v)}
          hint={'Example: {"adaptive_reuse": 0.9, "human_cost": 0.5}'}
        />
      </div>
      <div className="grid gap-4 md:grid-cols-[12rem_minmax(0,1fr)]">
        <TextField
          label="Editorial priority"
          type="number"
          value={item.editorial_priority ?? 0.5}
          onChange={(v) => set('editorial_priority', Number(v))}
          hint="0 to 1."
        />
        <TextAreaField
          label="Narrative value"
          value={item.narrative_value}
          onChange={(v) => set('narrative_value', v)}
          rows={2}
        />
      </div>
      <CsvField
        label="Visual or sensory cues"
        value={item.visual_cues}
        onChange={(v) => set('visual_cues', v)}
        hint="Short observable cues. Separate them with commas."
      />
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <h4 className="text-sm font-semibold text-slate-900">Connected recorded explanations</h4>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          Read the complete source recording before approving what this AI story adds.
        </p>
        {connectedRecordings.length === 0 ? (
          <p className="mt-3 text-sm text-amber-700">
            No anchor recording is connected. This can be intentional for a route- or area-level story.
          </p>
        ) : (
          <div className="mt-3 space-y-3">
            {connectedRecordings.map(({ stopId, shadow }) => (
              <div key={stopId} className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono text-xs font-semibold text-slate-600">{stopId}</span>
                  <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${
                    ['reviewed', 'not_applicable'].includes(shadow?.coverage_review_status)
                      && shadow?.reviewed_text_hash === recordedTextHash(shadow?.recorded_text)
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-amber-100 text-amber-800'
                  }`}>
                    {shadow
                      ? `${shadow.coverage_review_status || 'draft'} coverage`
                      : 'Missing Shadow record'}
                  </span>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                  {shadow?.recorded_text || 'No recorded script is available for this stop.'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PromiseEditor({ item, onChange }) {
  const set = (key, value) => onChange({ ...item, [key]: value });
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <TextField label="Promise ID" value={item.promise_id} onChange={(v) => set('promise_id', v)} />
        <TextField label="Visitor-facing label" value={item.label} onChange={(v) => set('label', v)} />
      </div>
      <TextAreaField
        label="Description"
        value={item.description}
        onChange={(v) => set('description', v)}
        rows={2}
        hint="What kind of payoff can Sofia credibly deliver?"
      />
      <div className="grid gap-4 md:grid-cols-2">
        <CsvField label="Internal topic IDs" value={item.topic_ids} onChange={(v) => set('topic_ids', v)} />
        <CsvField
          label="Opportunity IDs"
          value={item.opportunity_ids}
          onChange={(v) => set('opportunity_ids', v)}
        />
      </div>
      <CsvField
        label="Interest signals"
        value={item.stated_interests}
        onChange={(v) => set('stated_interests', v)}
        placeholder="architecture, social history, engineering"
      />
      <TextAreaField
        label="Primary question"
        value={item.primary_question}
        onChange={(v) => set('primary_question', v)}
        rows={2}
      />
      <div className="grid gap-4 md:grid-cols-2">
        <CsvField
          label="Secondary questions"
          value={item.secondary_questions}
          onChange={(v) => set('secondary_questions', v)}
        />
        <CsvField
          label="Strong editorial signals"
          value={item.strong_signals}
          onChange={(v) => set('strong_signals', v)}
        />
      </div>
    </div>
  );
}

function VisitorPromptEditor({ item, onChange }) {
  const set = (key, value) => onChange({ ...item, [key]: value });
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          label="Visitor choice ID"
          value={item.topic_prompt_id}
          onChange={(v) => set('topic_prompt_id', v)}
        />
        <TextField
          label="Title shown in the app"
          value={item.title}
          onChange={(v) => set('title', v)}
          placeholder="Architecture & Urban Life"
        />
      </div>
      <TextAreaField
        label="Plain-language description"
        value={item.description}
        onChange={(v) => set('description', v)}
        rows={2}
        hint="Tell a first-time visitor what kind of subject this means."
      />
      <Field
        label="Canonical visitor topics"
        hint="These IDs are shared with the visitor's long-term interests across guides."
      >
        <CanonicalTopicPicker
          selected={item.canonical_topic_ids}
          onChange={(value) => set('canonical_topic_ids', value)}
        />
      </Field>
      <div className="grid gap-4 md:grid-cols-2">
        <CsvField
          label="Story promise IDs"
          value={item.promise_ids}
          onChange={(v) => set('promise_ids', v)}
        />
        <CsvField
          label="Place hints"
          value={item.places}
          onChange={(v) => set('places', v)}
          placeholder="citadel, waterfront, old town"
        />
      </div>
    </div>
  );
}

function ShadowEditor({ item, onChange }) {
  const set = (key, value) => onChange({ ...item, [key]: value });
  const setCoverage = (key, value) => onChange({
    ...item,
    [key]: value,
    coverage_review_status: 'draft',
    reviewed_text_hash: '',
  });
  const currentTextHash = recordedTextHash(item.recorded_text);
  const coverageReviewIsCurrent = ['reviewed', 'not_applicable']
    .includes(item.coverage_review_status)
    && item.reviewed_text_hash === currentTextHash;
  const setCoverageStatus = (status) => onChange({
    ...item,
    recorded_text_hash: currentTextHash,
    coverage_review_status: status,
    reviewed_text_hash: status === 'draft' ? '' : currentTextHash,
  });

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <TextField label="Stop ID" value={item.stop_id} onChange={(v) => set('stop_id', v)} />
        <TextField
          label="Recording version"
          value={item.recording_version}
          onChange={(v) => set('recording_version', v)}
        />
        <SelectField
          label="Review status"
          value={item.review_status || 'draft'}
          onChange={(v) => set('review_status', v)}
          options={[
            { value: 'draft', label: 'Draft' },
            { value: 'reviewed', label: 'Reviewed' },
            { value: 'approved', label: 'Approved' },
          ]}
        />
      </div>
      <TextAreaField
        label="Recorded script"
        value={item.recorded_text}
        onChange={(v) => onChange(updateShadowRecordedText(item, v))}
        rows={8}
        hint="Imported from the current guide. This is source material for producing the Shadow."
      />
      <div className={`rounded-xl border p-4 ${
        coverageReviewIsCurrent
          ? 'border-emerald-200 bg-emerald-50'
          : 'border-amber-200 bg-amber-50'
      }`}>
        <div className="grid gap-4 md:grid-cols-2">
          <SelectField
            label="Recording-coverage review"
            value={item.coverage_review_status || 'draft'}
            onChange={setCoverageStatus}
            options={[
              { value: 'draft', label: 'Draft / needs review' },
              { value: 'reviewed', label: 'Reviewed mapping' },
              { value: 'not_applicable', label: 'Reviewed: mapping not applicable' },
            ]}
          />
          <Field label="Current script fingerprint">
            <div className="break-all rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-[11px] text-slate-500">
              {currentTextHash}
            </div>
          </Field>
        </div>
        <p className={`mt-3 text-xs font-semibold ${
          coverageReviewIsCurrent ? 'text-emerald-700' : 'text-amber-800'
        }`}>
          {coverageReviewIsCurrent
            ? 'This decision matches the current recorded script.'
            : 'Coverage is unreviewed or stale. Editing the script or its coverage returns it to draft.'}
        </p>
        {item.coverage_review_status === 'not_applicable' && (
          <div className="mt-3">
            <TextAreaField
              label="Why mapping is not applicable"
              value={item.coverage_exception_reason}
              onChange={(v) => set('coverage_exception_reason', v)}
              rows={2}
              hint="Required. Explain the reviewed exception; an empty mapping alone is not approval."
            />
          </div>
        )}
      </div>
      <TextAreaField
        label="Semantic summary"
        value={item.semantic_summary}
        onChange={(v) => setCoverage('semantic_summary', v)}
        rows={3}
        hint="What this stop already explains, independent of its exact wording."
      />
      <TextAreaField
        label="Main reveal or ending"
        value={item.main_reveal}
        onChange={(v) => setCoverage('main_reveal', v)}
        rows={2}
      />
      <div className="grid gap-4 md:grid-cols-2">
        <CsvField label="Covered claim IDs" value={item.claim_refs} onChange={(v) => setCoverage('claim_refs', v)} />
        <CsvField
          label="Avoid repeating"
          value={item.avoid_repeating}
          onChange={(v) => setCoverage('avoid_repeating', v)}
          placeholder="main anecdote, conclusion, overused framing"
        />
        <CsvField label="Anecdotes already used" value={item.anecdotes} onChange={(v) => setCoverage('anecdotes', v)} />
        <CsvField label="Arguments already made" value={item.arguments} onChange={(v) => setCoverage('arguments', v)} />
      </div>
    </div>
  );
}

function StringListEditor({ title, description, items, onChange }) {
  const add = () => onChange([...(items || []), '']);
  const update = (index, value) => onChange(items.map((item, i) => (i === index ? value : item)));
  const remove = (index) => onChange(items.filter((_, i) => i !== index));

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        </div>
        <button
          type="button"
          onClick={add}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </div>
      {(items || []).length === 0 ? (
        <p className="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-400">No rules added.</p>
      ) : (
        <div className="space-y-2">
          {items.map((item, index) => (
            <div key={index} className="flex gap-2">
              <textarea
                value={item}
                onChange={(event) => update(index, event.target.value)}
                rows={2}
                className={`${inputClass} resize-y`}
              />
              <button
                type="button"
                onClick={() => remove(index)}
                className="rounded-lg px-3 text-red-500 hover:bg-red-50"
                title="Remove rule"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AiGuideContentTab({
  itinerary,
  setItinerary,
  onPublishAiConfigToLinkedLanguages,
}) {
  const guideId = text(
    itinerary?.metadata?.parent_guide_id
    || itinerary?.parent_guide_id
    || itinerary?.metadata?.id
    || itinerary?.id
    || 'draft',
  );
  const storageKey = `guidesofia.ai-guide-content.${guideId}`;
  const fileInputRef = useRef(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [storyMapCollection, setStoryMapCollection] = useState('visitor_topic_prompts');
  const [packageData, setPackageData] = useState(null);
  const [loadedStorageKey, setLoadedStorageKey] = useState('');
  const [notice, setNotice] = useState('');
  const [researchBrief, setResearchBrief] = useState('');
  const [allowedDomains, setAllowedDomains] = useState('');
  const [researchSize, setResearchSize] = useState('focused');
  const [researchResult, setResearchResult] = useState(null);
  const [isResearching, setIsResearching] = useState(false);
  const [isSavingCloud, setIsSavingCloud] = useState(false);
  const [isPublishingCloud, setIsPublishingCloud] = useState(false);
  const [isLoadingCloud, setIsLoadingCloud] = useState(false);
  const [cloudStorage, setCloudStorage] = useState(null);
  const [previewOpportunityId, setPreviewOpportunityId] = useState('');
  const [previewTopicIds, setPreviewTopicIds] = useState([]);
  const [previewDepth, setPreviewDepth] = useState('standard');
  const [previewRuns, setPreviewRuns] = useState([]);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [selected, setSelected] = useState({
    sources: 0,
    claims: 0,
    topics: 0,
    story_opportunities: 0,
    story_promises: 0,
    visitor_topic_prompts: 0,
    guide_shadow: 0,
  });

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(storageKey);
      setPackageData(saved ? normalizeAiGuidePackage(JSON.parse(saved)) : null);
      setLoadedStorageKey(storageKey);
    } catch (error) {
      setPackageData(null);
      setLoadedStorageKey(storageKey);
      setNotice(`The local AI content draft could not be read: ${error.message}`);
    }
  }, [storageKey]);

  useEffect(() => {
    if (!packageData || loadedStorageKey !== storageKey) return;
    window.localStorage.setItem(storageKey, JSON.stringify(packageData));
  }, [loadedStorageKey, packageData, storageKey]);

  const previewStorageKey = `${storageKey}.preview-runs`;
  useEffect(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(previewStorageKey) || '[]');
      setPreviewRuns(Array.isArray(saved) ? saved.slice(0, 50) : []);
    } catch {
      setPreviewRuns([]);
    }
  }, [previewStorageKey]);

  useEffect(() => {
    window.localStorage.setItem(previewStorageKey, JSON.stringify(previewRuns.slice(0, 50)));
  }, [previewRuns, previewStorageKey]);

  useEffect(() => {
    if (researchBrief.trim()) return;
    const metadata = itinerary?.metadata || {};
    const place = [metadata.name, metadata.city, metadata.country].filter(Boolean).join(', ');
    setResearchBrief(
      `Research strong, place-specific subjects for ${place || 'this guide'}. `
      + 'Find stories that complement the recorded guide, with particular attention to '
      + 'architecture, local life, people, engineering, landscape, and surprising connections '
      + 'that can support new personalized AI stops.',
    );
  }, [itinerary, researchBrief]);

  const validation = useMemo(
    () => (packageData ? validateAiGuidePackage(packageData) : null),
    [packageData],
  );

  const configuredAi5Mode = itinerary?.metadata?.sofia_ai?.enabled === true
    ? (itinerary?.metadata?.sofia_ai?.content_mode
      || packageData?.manifest?.ai_content_mode
      || (itinerary?.metadata?.sofia_ai?.knowledge_policy === 'beyond' ? 'mixed' : 'official'))
    : 'off';
  const ai5Mode = ['official', 'llm', 'mixed'].includes(configuredAi5Mode)
    ? configuredAi5Mode
    : 'off';

  const setAi5Mode = (mode) => {
    if (!setItinerary) return;
    const enabled = mode !== 'off';
    setItinerary((current) => ({
      ...current,
      metadata: {
        ...(current.metadata || {}),
        sofia_ai: {
          ...(current.metadata?.sofia_ai || {}),
          enabled,
          content_mode: enabled ? mode : current.metadata?.sofia_ai?.content_mode,
          knowledge_policy: 'official',
        },
      },
    }));
    if (enabled) {
      setPackageData((current) => current ? ({
        ...current,
        manifest: {
          ...current.manifest,
          ai_content_mode: mode,
          default_knowledge_policy: 'official',
        },
      }) : current);
    }
  };

  const updateManifest = (key, value) => {
    setPackageData((current) => ({
      ...current,
      manifest: {
        ...current.manifest,
        [key]: value,
        ...(key === 'language' && value !== current.manifest.language
          ? {
            visitor_language_review: {
              ...(current.manifest.visitor_language_review || {}),
              status: 'draft',
              reviewed_at: '',
            },
          }
          : {}),
      },
    }));
  };

  const setVisitorLanguageReviewed = (reviewed) => {
    setPackageData((current) => ({
      ...current,
      manifest: {
        ...current.manifest,
        visitor_language_review: {
          ...(current.manifest.visitor_language_review || {}),
          status: reviewed ? 'reviewed' : 'draft',
          language: reviewed ? current.manifest.language : '',
          reviewed_input_hash: reviewed ? sha256Text(visitorLanguageReviewInput(current)) : '',
          reviewed_at: reviewed ? new Date().toISOString() : '',
        },
      },
    }));
  };

  const updateVisitorLanguageReviewNote = (reviewerNote) => {
    setPackageData((current) => ({
      ...current,
      manifest: {
        ...current.manifest,
        visitor_language_review: {
          ...(current.manifest.visitor_language_review || {}),
          reviewer_note: reviewerNote,
        },
      },
    }));
  };

  const setExperienceContractVersion = (value) => {
    setPackageData((current) => {
      const manifest = { ...current.manifest };
      if (value === 'legacy') {
        delete manifest.experience_contract_version;
      } else {
        manifest.experience_contract_version = EXPERIENCE_CONTRACT_VERSION;
      }
      return { ...current, manifest };
    });
  };

  const updateCollectionItem = (collection, index, value) => {
    setPackageData((current) => ({
      ...current,
      [collection]: current[collection].map((item, itemIndex) => (
        itemIndex === index ? value : item
      )),
    }));
  };

  const addCollectionItem = (collection) => {
    setPackageData((current) => {
      const next = [...current[collection], createAiGuideRecord(collection, current)];
      setSelected((state) => ({ ...state, [collection]: next.length - 1 }));
      return { ...current, [collection]: next };
    });
  };

  const deleteCollectionItem = (collection, index) => {
    const record = packageData[collection][index];
    const identifier = Object.entries(record || {}).find(([key]) => key.endsWith('_id'))?.[1];
    if (!window.confirm(`Delete ${identifier || 'this record'}? References will not be removed automatically.`)) {
      return;
    }
    setPackageData((current) => ({
      ...current,
      [collection]: current[collection].filter((_, itemIndex) => itemIndex !== index),
    }));
    setSelected((state) => ({ ...state, [collection]: Math.max(0, index - 1) }));
  };

  const createStarter = () => {
    if (packageData && !window.confirm('Replace the current local AI content draft with a new package from this guide?')) {
      return;
    }
    setPackageData(createAiGuidePackage(itinerary));
    setActiveSection('overview');
    setNotice('Starter package created from the current guide stops and recorded scripts.');
  };

  const syncStops = () => {
    const starter = createAiGuidePackage(itinerary);
    setPackageData((current) => {
      const currentStops = new Map(current.stops.map((stop) => [stop.stop_id, stop]));
      const currentShadow = new Map(current.guide_shadow.map((shadow) => [shadow.stop_id, shadow]));
      return {
        ...current,
        stops: starter.stops.map((stop) => {
          const existing = currentStops.get(stop.stop_id) || {};
          return {
            ...existing,
            ...stop,
            names: {
              ...(existing.names || {}),
              ...stop.names,
              aliases: existing.names?.aliases || stop.names.aliases,
            },
            claim_refs: existing.claim_refs || stop.claim_refs,
            visual_cue_refs: existing.visual_cue_refs || stop.visual_cue_refs,
            good_followup_questions:
              existing.good_followup_questions || stop.good_followup_questions,
          };
        }),
        guide_shadow: starter.guide_shadow.map((shadow) => ({
          ...syncAiGuideShadowRecordedText(
            {
              ...shadow,
              ...(currentShadow.get(shadow.stop_id) || {}),
            },
            shadow.recorded_text,
          ),
        })),
      };
    });
    setNotice('Stops and recorded scripts were synchronized. Existing editorial Shadow fields were preserved.');
  };

  const importPackage = async (event) => {
    const [file] = event.target.files || [];
    event.target.value = '';
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      const normalized = normalizeAiGuidePackage(parsed);
      const result = validateAiGuidePackage(normalized);
      setPackageData(normalized);
      setActiveSection('validation');
      setNotice(
        `Imported ${file.name}: ${result.errors} error${result.errors === 1 ? '' : 's'} and ${result.warnings} warning${result.warnings === 1 ? '' : 's'}.`,
      );
    } catch (error) {
      setNotice(`Import failed: ${error.message}`);
    }
  };

  const downloadPackage = () => {
    const blob = new Blob(
      [JSON.stringify(normalizeAiGuidePackage(packageData), null, 2)],
      { type: 'application/json' },
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = aiGuidePackageFilename(packageData);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    setNotice(`Exported ${aiGuidePackageFilename(packageData)}.`);
  };

  const runAutomaticResearch = async () => {
    if (!packageData) return;
    setIsResearching(true);
    setResearchResult(null);
    setNotice('');
    try {
      const size = researchSize === 'broad'
        ? { maxSources: 18, maxClaims: 45 }
        : { maxSources: 10, maxClaims: 24 };
      const result = await aiGuideContentService.research({
        itinerary,
        packageData,
        researchBrief,
        allowedDomains: toList(allowedDomains),
        ...size,
      });
      setResearchResult(result);
      setNotice(
        `Research completed with ${result.consultedSources?.length || 0} consulted web sources. `
        + 'Nothing has been added to the package yet.',
      );
    } catch (error) {
      const retry = error.retryAfterSeconds
        ? ` Try again in ${error.retryAfterSeconds} seconds.`
        : '';
      setNotice(`Automatic research failed: ${error.message}.${retry}`);
    } finally {
      setIsResearching(false);
    }
  };

  const addResearchDraft = () => {
    if (!researchResult) return;
    setPackageData((current) => mergeAiGuideResearchDraft(current, researchResult));
    setResearchResult(null);
    setNotice(
      'Research proposals were added as drafts. Review sources and claims before marking anything official-safe.',
    );
  };

  const savePackageToCloud = async () => {
    if (!packageData) return;
    if (['approved', 'published'].includes(packageData.manifest.status) && !validation.valid) {
      setNotice('Resolve validation errors before saving an approved or published package.');
      setActiveSection('validation');
      return;
    }

    setIsSavingCloud(true);
    setNotice('');
    try {
      const result = await aiGuideContentService.save({ itinerary, packageData });
      setPackageData(normalizeAiGuidePackage(result.packageData));
      setCloudStorage(result.storage || null);
      setNotice(
        `Saved version ${result.packageData?.manifest?.content_version} to Firebase Storage. `
        + 'A permanent revision and latest.json were created.',
      );
    } catch (error) {
      setNotice(`Cloud save failed: ${error.message}`);
    } finally {
      setIsSavingCloud(false);
    }
  };

  const publishPackageToCloud = async () => {
    if (!packageData) return;
    if (!validation.valid) {
      setNotice('Resolve validation errors before publishing the AI runtime package.');
      setActiveSection('validation');
      return;
    }

    setIsPublishingCloud(true);
    setNotice('');
    try {
      const result = await aiGuideContentService.publish({ itinerary, packageData });
      const savedPackage = normalizeAiGuidePackage(result.packageData);
      const runtimeUrl = result.storage?.publishedPublicUrl;
      if (!runtimeUrl) throw new Error('The backend did not return a runtime package URL.');
      setPackageData(savedPackage);
      setCloudStorage(result.storage || null);
      const publishedAiConfig = {
        ...(itinerary?.metadata?.sofia_ai || {}),
        enabled: itinerary?.metadata?.sofia_ai?.enabled === true,
        experience_version: 'ai5',
        package_id: savedPackage.manifest.treasure_id,
        runtime_package_url: runtimeUrl,
        canonical_guide_id: savedPackage.manifest.guide_id,
        canonical_language: savedPackage.manifest.language,
        knowledge_policy: savedPackage.manifest.default_knowledge_policy,
        content_mode: savedPackage.manifest.ai_content_mode,
        content_version: savedPackage.manifest.content_version,
      };
      if (setItinerary) {
        setItinerary((current) => ({
          ...current,
          metadata: {
            ...(current.metadata || {}),
            sofia_knowledge_policy: savedPackage.manifest.default_knowledge_policy,
            sofia_ai: publishedAiConfig,
          },
        }));
      }
      const linkedLanguageCount = onPublishAiConfigToLinkedLanguages
        ? await onPublishAiConfigToLinkedLanguages({
          sofiaAi: publishedAiConfig,
          sofiaKnowledgePolicy: savedPackage.manifest.default_knowledge_policy,
        })
        : 0;
      setNotice(
        `Published runtime version ${savedPackage.manifest.content_version}. `
        + `Its immutable URL was added to this guide JSON and ${linkedLanguageCount} linked language `
        + `${linkedLanguageCount === 1 ? 'guide was' : 'guides were'} updated. Save this guide to persist its own metadata.`,
      );
    } catch (error) {
      const details = error.validationErrors?.length
        ? ` ${error.validationErrors.join(' ')}`
        : '';
      setNotice(`Publication failed: ${error.message}.${details}`);
    } finally {
      setIsPublishingCloud(false);
    }
  };

  const loadPackageFromCloud = async () => {
    if (packageData && !window.confirm('Replace the current local draft with the latest Firebase package?')) {
      return;
    }

    setIsLoadingCloud(true);
    setNotice('');
    try {
      const result = await aiGuideContentService.load({ itinerary });
      setPackageData(normalizeAiGuidePackage(result.packageData));
      setCloudStorage(result.registry || null);
      setActiveSection('overview');
      setNotice(
        `Loaded saved AI content version ${result.registry?.contentVersion || result.packageData?.manifest?.content_version}.`,
      );
    } catch (error) {
      setNotice(`Cloud load failed: ${error.message}`);
    } finally {
      setIsLoadingCloud(false);
    }
  };

  const previewRuntimeUrl = cloudStorage?.publishedPublicUrl
    || itinerary?.metadata?.sofia_ai?.runtime_package_url
    || '';

  const togglePreviewTopic = (topicId) => {
    setPreviewTopicIds((current) => (
      current.includes(topicId)
        ? current.filter((id) => id !== topicId)
        : [...current, topicId].slice(0, 12)
    ));
  };

  const runRealPreview = async () => {
    const opportunity = packageData.story_opportunities.find(
      (item) => item.opportunity_id === previewOpportunityId,
    );
    setIsPreviewing(true);
    setNotice('');
    try {
      const artifact = await aiGuidePreviewService.run({
        packageData,
        runtimePackageUrl: previewRuntimeUrl,
        opportunity,
        selectedTopicIds: previewTopicIds,
        depth: previewDepth,
      });
      setPreviewRuns((current) => [artifact, ...current].slice(0, 50));
      setNotice('Real provider preview completed. Its first output and diagnostics were preserved.');
    } catch (error) {
      const artifact = error.previewArtifact || {
        artifact_version: 'sofia_ai_preview_run.v1',
        run_id: `builder_preview_failed_${Date.now()}`,
        started_at: new Date().toISOString(),
        completed_at: new Date().toISOString(),
        provider_output: false,
        status: 'failed',
        opportunity_id: previewOpportunityId || null,
        profile: { selected_topic_ids: previewTopicIds, depth: previewDepth },
        failure: { message: error.message, code: error.code || null },
      };
      setPreviewRuns((current) => [artifact, ...current].slice(0, 50));
      setNotice(`Real preview failed and the failure was preserved: ${error.message}`);
    } finally {
      setIsPreviewing(false);
    }
  };

  const downloadPreviewRuns = () => {
    const blob = new Blob([JSON.stringify({
      artifact_collection_version: 'sofia_ai_preview_collection.v1',
      exported_at: new Date().toISOString(),
      runs: previewRuns,
    }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `sofia-ai-preview-runs-${guideId}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const renderCollection = (collection, config, editor) => (
    <CollectionWorkspace
      items={packageData[collection]}
      selectedIndex={selected[collection]}
      onSelect={(index) => setSelected((state) => ({ ...state, [collection]: index }))}
      onAdd={() => addCollectionItem(collection)}
      onDelete={(index) => deleteCollectionItem(collection, index)}
      titleFor={(item) => text(item?.[config.titleKey])}
      idFor={(item) => text(item?.[config.idKey])}
      emptyLabel={config.label}
    >
      {(item, index) => editor({
        item,
        onChange: (value) => updateCollectionItem(collection, index, value),
      })}
    </CollectionWorkspace>
  );

  const renderSection = () => {
    if (activeSection === 'overview') {
      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">Package setup</h2>
            <p className="mt-1 max-w-3xl text-sm text-slate-500">
              This package stays separate from the normal guide JSON. It is the governed knowledge
              layer used to plan personalized AI stops without sending the full corpus to narration.
            </p>
          </div>
          <div className="rounded-xl border-2 border-pink-500 bg-pink-100 p-5 shadow-[0_0_0_4px_rgba(236,72,153,0.12)]">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="mb-2 inline-flex rounded-full bg-pink-600 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-white">
                  New AI5 control
                </div>
                <h3 className="font-semibold text-pink-950">AI tab</h3>
                <p className="mt-1 max-w-3xl text-sm text-pink-900">
                  Choose whether the visitor sees no AI tab, Treasure-grounded stories, broader
                  LLM stories, or both. Publishing never changes this choice automatically.
                </p>
              </div>
              <div className="min-w-[280px] rounded-lg bg-pink-600 p-3 shadow-lg ring-2 ring-pink-300">
                <label className="mb-1 block text-[10px] font-black uppercase tracking-[0.16em] text-pink-100">
                  Visitor AI mode
                </label>
                <select
                  value={ai5Mode}
                  onChange={(event) => setAi5Mode(event.target.value)}
                  className="w-full rounded-md border border-pink-300 bg-white px-3 py-2 text-sm font-bold text-pink-950 outline-none focus:ring-2 focus:ring-pink-200"
                >
                  <option value="off">No AI tab</option>
                  <option value="official">Official: Treasure only</option>
                  <option value="llm">LLM only: no Treasure claims</option>
                  <option value="mixed">Both: Official + LLM</option>
                </select>
              </div>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Registered sources" value={packageData.sources.length} />
            <Stat label="Atomic claims" value={packageData.claims.length} tone="blue" />
            <Stat label="Story opportunities" value={packageData.story_opportunities.length} tone="green" />
            <Stat label="Validation errors" value={validation.errors} tone={validation.errors ? 'amber' : 'green'} />
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="mb-4 font-semibold text-slate-900">Manifest</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <TextField
                label="Treasure ID"
                value={packageData.manifest.treasure_id}
                onChange={(v) => updateManifest('treasure_id', v)}
              />
              <TextField
                label="Guide ID"
                value={packageData.manifest.guide_id}
                onChange={(v) => updateManifest('guide_id', v)}
              />
              <TextField
                label="Language"
                value={packageData.manifest.language}
                onChange={(v) => updateManifest('language', v)}
              />
              <TextField
                label="Content version"
                type="number"
                value={packageData.manifest.content_version}
                onChange={(v) => updateManifest('content_version', Number.parseInt(v, 10) || 1)}
              />
              <Field
                label="AI content mode"
                hint="Change this with the bright-pink Visitor AI mode selector above."
              >
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">
                  {{ official: 'Official: Treasure only', llm: 'LLM only: no Treasure claims', mixed: 'Both: Official + LLM' }[packageData.manifest.ai_content_mode] || 'Official: Treasure only'}
                </div>
              </Field>
              <SelectField
                label="Package status"
                value={packageData.manifest.status}
                onChange={(v) => updateManifest('status', v)}
                options={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'in_review', label: 'In review' },
                  { value: 'approved', label: 'Approved' },
                  { value: 'published', label: 'Published' },
                ]}
              />
              <SelectField
                label="Experience contract"
                value={packageData.manifest.experience_contract_version === EXPERIENCE_CONTRACT_VERSION
                  ? String(EXPERIENCE_CONTRACT_VERSION)
                  : 'legacy'}
                onChange={setExperienceContractVersion}
                options={[
                  { value: 'legacy', label: 'Legacy (migration warnings only)' },
                  { value: String(EXPERIENCE_CONTRACT_VERSION), label: 'Version 1 (review required)' },
                ]}
              />
              <Field
                label="Visitor-language review"
                hint="Review all visitor-facing AI text. Changing the language or reviewed text makes this review out of date."
              >
                <label className="flex min-h-[42px] items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                  <input
                    type="checkbox"
                    checked={packageData.manifest.visitor_language_review?.status === 'reviewed'
                      && packageData.manifest.visitor_language_review?.language
                        === packageData.manifest.language
                      && (packageData.manifest.quality_contract_version !== 1
                        || packageData.manifest.visitor_language_review?.reviewed_input_hash
                          === sha256Text(visitorLanguageReviewInput(packageData)))}
                    onChange={(event) => setVisitorLanguageReviewed(event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-sky-700 focus:ring-sky-500"
                  />
                  Reviewed in {packageData.manifest.language}
                </label>
              </Field>
              <TextField
                label="Language review note"
                value={packageData.manifest.visitor_language_review?.reviewer_note || ''}
                onChange={updateVisitorLanguageReviewNote}
                placeholder="Reviewer, scope, or fallback decisions"
              />
            </div>
            <p className="mt-4 text-xs leading-5 text-slate-500">
              Version 1 blocks Builder publication until every story contribution and recorded-script
              coverage decision is current and all visitor-facing copy has been reviewed in the package
              language. Keep legacy selected while migrating existing packages.
            </p>
          </div>
          <div className="rounded-xl border border-sky-200 bg-sky-50 p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-sky-950">Recorded guide connection</h3>
                <p className="mt-1 text-sm text-sky-800">
                  Refresh stop names, coordinates, and scripts from the currently loaded guide while
                  keeping reviewed claims and Shadow notes.
                </p>
              </div>
              <button
                type="button"
                onClick={syncStops}
                className="inline-flex items-center gap-2 rounded-lg bg-sky-900 px-4 py-2 text-sm font-medium text-white hover:bg-sky-800"
              >
                <RefreshCw className="h-4 w-4" />
                Sync guide stops
              </button>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-slate-900">Saved JSON package</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Local autosave protects browser work. Save JSON creates an immutable revision and
                  updates the guide's latest package in Firebase.
                </p>
                {cloudStorage?.latestStoragePath && (
                  <p className="mt-2 break-all font-mono text-xs text-slate-400">
                    {cloudStorage.latestStoragePath}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {cloudStorage?.publishedPublicUrl && (
                  <a
                    href={cloudStorage.publishedPublicUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open runtime JSON
                  </a>
                )}
                <button
                  type="button"
                  onClick={savePackageToCloud}
                  disabled={isSavingCloud}
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {isSavingCloud
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <CloudUpload className="h-4 w-4" />}
                  Save JSON
                </button>
                <button
                  type="button"
                  onClick={publishPackageToCloud}
                  disabled={isPublishingCloud || !validation.valid}
                  className="inline-flex items-center gap-2 rounded-lg bg-pink-600 px-4 py-2 text-sm font-bold text-white shadow-md ring-2 ring-pink-200 hover:bg-pink-500 disabled:opacity-50"
                >
                  {isPublishingCloud
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <ShieldCheck className="h-4 w-4" />}
                  Publish AI runtime
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (activeSection === 'research') {
      const draft = researchResult?.draft;
      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">Automatic research</h2>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
              Sofia searches the web, compares findings with the recorded guide, and proposes
              sources, claims, visitor themes, story opportunities, and Guide Shadow updates.
              Results stay outside the package until you add them as a draft.
            </p>
          </div>

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            Web research is never automatically approved. Imported claims are marked as draft,
            disabled for Official mode, and retain their source URL and research run.
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="space-y-4">
              <TextAreaField
                label="Research brief"
                value={researchBrief}
                onChange={setResearchBrief}
                rows={6}
                hint="Describe useful areas to investigate, known gaps, audiences, or partner priorities."
              />
              <TextField
                label="Allowed domains"
                value={allowedDomains}
                onChange={setAllowedDomains}
                placeholder="cm-cascais.pt, patrimoniocultural.gov.pt"
                hint="Optional comma-separated allowlist. Leave empty to search broadly while blocking low-quality aggregators."
              />
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  {
                    id: 'focused',
                    title: 'Focused research',
                    description: 'Up to 10 sources and 24 claims. Best for normal editorial passes.',
                  },
                  {
                    id: 'broad',
                    title: 'Broader research',
                    description: 'Up to 18 sources and 45 claims. Slower and more expensive.',
                  },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setResearchSize(option.id)}
                    className={`rounded-xl border p-4 text-left transition ${
                      researchSize === option.id
                        ? 'border-sky-500 bg-sky-50'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <span className="block text-sm font-semibold text-slate-900">
                      {option.title}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-slate-500">
                      {option.description}
                    </span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={runAutomaticResearch}
                disabled={isResearching || researchBrief.trim().length < 10}
                className="inline-flex items-center gap-2 rounded-lg bg-sky-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isResearching
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Globe2 className="h-4 w-4" />}
                {isResearching ? 'Researching the guide...' : 'Research and build draft'}
              </button>
            </div>
          </div>

          {isResearching && (
            <div className="rounded-xl border border-sky-200 bg-sky-50 p-5">
              <div className="flex items-start gap-3">
                <Loader2 className="mt-0.5 h-5 w-5 animate-spin text-sky-700" />
                <div>
                  <h3 className="font-semibold text-sky-950">Research run in progress</h3>
                  <p className="mt-1 text-sm leading-6 text-sky-800">
                    The model may perform several searches and open multiple sources. Keep this page
                    open until the draft returns.
                  </p>
                </div>
              </div>
            </div>
          )}

          {draft && (
            <div className="space-y-5">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
                <h3 className="font-semibold text-emerald-950">Research draft ready</h3>
                <p className="mt-2 text-sm leading-6 text-emerald-800">
                  {draft.research_summary}
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  <Stat label="Sources" value={draft.sources?.length || 0} />
                  <Stat label="Claims" value={draft.claims?.length || 0} />
                  <Stat label="Topics" value={draft.topics?.length || 0} />
                  <Stat
                    label="Opportunities"
                    value={draft.story_opportunities?.length || 0}
                  />
                  <Stat label="Promises" value={draft.story_promises?.length || 0} />
                  <Stat
                    label="Visitor choices"
                    value={draft.visitor_topic_prompts?.length || 0}
                  />
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="font-semibold text-slate-900">Consulted web sources</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Complete URLs returned by the web-search tool, including sources not cited in the
                  final draft.
                </p>
                <div className="mt-4 max-h-72 space-y-2 overflow-auto">
                  {(researchResult.consultedSources || []).map((source) => (
                    <a
                      key={source.url}
                      href={source.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-start gap-3 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:border-sky-300 hover:bg-sky-50"
                    >
                      <ExternalLink className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                      <span className="min-w-0">
                        <span className="block font-medium">
                          {source.title || source.url}
                        </span>
                        <span className="block truncate text-xs text-slate-400">{source.url}</span>
                      </span>
                    </a>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={addResearchDraft}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"
                >
                  <Plus className="h-4 w-4" />
                  Add proposals to package as draft
                </button>
                <button
                  type="button"
                  onClick={() => setResearchResult(null)}
                  className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Discard research result
                </button>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (activeSection === 'sources') {
      return renderCollection(
        'sources',
        { label: 'Sources', idKey: 'source_id', titleKey: 'title' },
        ({ item, onChange }) => <SourceEditor item={item} onChange={onChange} />,
      );
    }

    if (activeSection === 'claims') {
      return renderCollection(
        'claims',
        { label: 'Claims', idKey: 'claim_id', titleKey: 'text' },
        ({ item, onChange }) => <ClaimEditor item={item} onChange={onChange} />,
      );
    }

    if (activeSection === 'story-map') {
      const config = STORY_MAP_COLLECTIONS.find((item) => item.id === storyMapCollection);
      const editors = {
        topics: ({ item, onChange }) => <TopicEditor item={item} onChange={onChange} />,
        story_opportunities: ({ item, onChange }) => (
          <OpportunityEditor item={item} onChange={onChange} packageData={packageData} />
        ),
        story_promises: ({ item, onChange }) => <PromiseEditor item={item} onChange={onChange} />,
        visitor_topic_prompts: ({ item, onChange }) => <VisitorPromptEditor item={item} onChange={onChange} />,
      };
      return (
        <div className="space-y-5">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">Story Map</h2>
            <p className="mt-1 text-sm text-slate-500">
              Connect factual ingredients to coherent story possibilities and understandable choices
              for a visitor who does not yet know what this guide contains.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {STORY_MAP_COLLECTIONS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setStoryMapCollection(item.id)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                  storyMapCollection === item.id
                    ? 'bg-sky-900 text-white'
                    : 'border border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                {item.label}
                <span className="ml-2 opacity-60">{packageData[item.id].length}</span>
              </button>
            ))}
          </div>
          {renderCollection(config.id, config, editors[config.id])}
        </div>
      );
    }

    if (activeSection === 'shadow') {
      return (
        <div className="space-y-5">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">Guide Shadow</h2>
            <p className="mt-1 text-sm text-slate-500">
              Record what the official audio already reveals, argues, and concludes. This is how Sofia
              avoids giving the visitor a rewritten version of something they can already hear.
            </p>
          </div>
          {renderCollection(
            'guide_shadow',
            { label: 'Shadow records', idKey: 'stop_id', titleKey: 'stop_id' },
            ({ item, onChange }) => <ShadowEditor item={item} onChange={onChange} />,
          )}
        </div>
      );
    }

    if (activeSection === 'preview') {
      const latestRun = previewRuns[0] || null;
      const namespace = process.env.REACT_APP_AI_GUIDE_PREVIEW_NAMESPACE || '';
      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">Real AI preview</h2>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
              Runs the authenticated app story endpoint against the immutable published runtime
              package. These are real provider outputs, not simulations. Every first output and
              failure is retained locally as an evaluation artifact.
            </p>
          </div>
          <div className={`rounded-xl border p-4 text-sm ${
            previewRuntimeUrl && namespace
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}>
            <div className="font-semibold">
              {previewRuntimeUrl ? 'Published runtime found' : 'No published runtime URL found'}
            </div>
            <div className="mt-1 break-all text-xs opacity-80">
              {previewRuntimeUrl || 'Publish or load a runtime package first.'}
            </div>
            <div className="mt-2 text-xs">
              {namespace
                ? `Isolated evaluation namespace: ${namespace}`
                : 'Set REACT_APP_AI_GUIDE_PREVIEW_NAMESPACE before enabling billable previews.'}
            </div>
          </div>
          <div className="grid gap-5 rounded-xl border border-slate-200 bg-white p-5 lg:grid-cols-2">
            <SelectField
              label="Story opportunity"
              value={previewOpportunityId}
              onChange={setPreviewOpportunityId}
              options={[
                { value: '', label: 'Choose an opportunity' },
                ...packageData.story_opportunities.map((item) => ({
                  value: item.opportunity_id,
                  label: item.discovery_title || item.opportunity_id,
                })),
              ]}
              hint="The backend resolves the authoritative question, contribution, lens and evidence."
            />
            <SelectField
              label="Requested depth"
              value={previewDepth}
              onChange={setPreviewDepth}
              options={[
                { value: 'brief', label: 'Brief' },
                { value: 'standard', label: 'Standard' },
                { value: 'deep', label: 'Deep' },
              ]}
            />
            <div className="lg:col-span-2">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Visitor profile — leave empty for neutral
              </div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {CANONICAL_VISITOR_TOPICS.map((topic) => {
                  const active = previewTopicIds.includes(topic.id);
                  return (
                    <button
                      key={topic.id}
                      type="button"
                      aria-pressed={active}
                      onClick={() => togglePreviewTopic(topic.id)}
                      className={`rounded-lg border px-3 py-2 text-left text-sm ${
                        active
                          ? 'border-sky-500 bg-sky-50 text-sky-900'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      {topic.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 lg:col-span-2">
              <button
                type="button"
                onClick={runRealPreview}
                disabled={isPreviewing || !previewRuntimeUrl || !namespace || !previewOpportunityId}
                className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {isPreviewing
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Play className="h-4 w-4" />}
                {isPreviewing ? 'Running real pipeline...' : 'Run and preserve first output'}
              </button>
              <span className="text-xs text-slate-500">
                This can incur provider cost. Retrying creates a separate recorded attempt.
              </span>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-slate-900">Evaluation artifacts</h3>
                <p className="mt-1 text-sm text-slate-500">
                  {previewRuns.length} run{previewRuns.length === 1 ? '' : 's'} retained in this browser.
                </p>
              </div>
              <button
                type="button"
                onClick={downloadPreviewRuns}
                disabled={!previewRuns.length}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 disabled:opacity-40"
              >
                <Download className="h-4 w-4" />
                Export all runs
              </button>
            </div>
            {latestRun ? (
              <div className="mt-5 space-y-4 border-t border-slate-100 pt-5">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className={`rounded-full px-2 py-1 font-semibold ${
                    latestRun.status === 'completed'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {latestRun.status}
                  </span>
                  <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600">
                    {latestRun.provider_output ? 'real provider output' : 'preflight failure'}
                  </span>
                  <span className="rounded-full bg-slate-100 px-2 py-1 font-mono text-slate-600">
                    {latestRun.run_id}
                  </span>
                </div>
                {latestRun.output?.text && (
                  <div className="grid gap-4 lg:grid-cols-2">
                    <div className="rounded-lg border border-slate-200 p-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Connected recording(s)</div>
                      {(latestRun.connected_recordings || []).length ? (
                        latestRun.connected_recordings.map((recording) => (
                          <div key={recording.stop_id} className="mt-3 border-t border-slate-100 pt-3 first:border-0 first:pt-0">
                            <div className="font-mono text-[11px] text-slate-400">{recording.stop_id}</div>
                            <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                              {recording.recorded_text || recording.semantic_summary || 'No recording transcript stored.'}
                            </p>
                          </div>
                        ))
                      ) : (
                        <p className="mt-2 text-sm text-slate-500">No connected recording transcript.</p>
                      )}
                    </div>
                    <div className="rounded-lg border border-sky-200 bg-sky-50/40 p-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-sky-700">First AI narration</div>
                      <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-800">
                        {latestRun.output.text}
                      </p>
                    </div>
                  </div>
                )}
                {latestRun.failure?.message && (
                  <p className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
                    {latestRun.failure.message}
                  </p>
                )}
                <details className="rounded-lg border border-slate-200 p-3">
                  <summary className="cursor-pointer text-sm font-medium text-slate-700">
                    Plan, evidence, cache and usage diagnostics
                  </summary>
                  <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap text-xs text-slate-600">
                    {JSON.stringify(latestRun, null, 2)}
                  </pre>
                </details>
              </div>
            ) : (
              <p className="mt-5 border-t border-slate-100 pt-5 text-sm text-slate-500">
                No real preview has been run yet.
              </p>
            )}
          </div>
        </div>
      );
    }

    if (activeSection === 'governance') {
      const updateRules = (key, value) => setPackageData((current) => ({
        ...current,
        governance: { ...current.governance, [key]: value },
      }));
      return (
        <div className="space-y-5">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">Governance</h2>
            <p className="mt-1 text-sm text-slate-500">
              Rules here apply to every generated story and answer for this guide.
            </p>
          </div>
          <StringListEditor
            title="Forbidden claims"
            description="Claims or framings Sofia must never present."
            items={packageData.governance.forbidden_claims}
            onChange={(value) => updateRules('forbidden_claims', value)}
          />
          <StringListEditor
            title="Required framing"
            description="Editorial context, uncertainty language, and attribution rules."
            items={packageData.governance.required_framing}
            onChange={(value) => updateRules('required_framing', value)}
          />
          <StringListEditor
            title="Sensitive topics"
            description="Subjects requiring special care or human review."
            items={packageData.governance.sensitive_topics}
            onChange={(value) => updateRules('sensitive_topics', value)}
          />
          <StringListEditor
            title="Known limitations"
            description="Coverage gaps or operational facts that must be checked before publication."
            items={packageData.governance.pilot_limitations}
            onChange={(value) => updateRules('pilot_limitations', value)}
          />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-950">Validate & export</h2>
          <p className="mt-1 text-sm text-slate-500">
            Validation checks the same relationships the visitor app depends on. Warnings may remain
            during editorial work; errors should be resolved before publishing.
          </p>
        </div>
        <div className={`rounded-xl border p-5 ${
          validation.valid
            ? 'border-emerald-200 bg-emerald-50'
            : 'border-red-200 bg-red-50'
        }`}>
          <div className="flex items-start gap-3">
            {validation.valid
              ? <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
              : <AlertCircle className="mt-0.5 h-5 w-5 text-red-600" />}
            <div>
              <h3 className={`font-semibold ${validation.valid ? 'text-emerald-900' : 'text-red-900'}`}>
                {validation.valid ? 'Package references are valid' : `${validation.errors} blocking error${validation.errors === 1 ? '' : 's'}`}
              </h3>
              <p className={`mt-1 text-sm ${validation.valid ? 'text-emerald-700' : 'text-red-700'}`}>
                {validation.warnings} editorial warning{validation.warnings === 1 ? '' : 's'}.
              </p>
            </div>
          </div>
        </div>
        {validation.issues.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            {validation.issues.map((issue, index) => (
              <div
                key={`${issue.path}-${index}`}
                className="flex gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0"
              >
                {issue.severity === 'error'
                  ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                  : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />}
                <div>
                  <p className="text-sm text-slate-800">{issue.message}</p>
                  <p className="mt-0.5 font-mono text-[11px] text-slate-400">{issue.path}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-slate-900">{aiGuidePackageFilename(packageData)}</h3>
              <p className="mt-1 text-sm text-slate-500">
                Export is always available for review. Publishing and Firebase versioning come in the
                next implementation slice.
              </p>
            </div>
            <button
              type="button"
              onClick={downloadPackage}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              <Download className="h-4 w-4" />
              Export JSON
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-6 overflow-hidden rounded-2xl bg-slate-950 text-white shadow-lg">
        <div className="relative px-6 py-7 sm:px-8">
          <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-sky-400/20 blur-3xl" />
          <div className="relative flex flex-wrap items-start justify-between gap-5">
            <div className="max-w-3xl">
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-sky-300">
                <Layers3 className="h-4 w-4" />
                Sofia production workspace
              </div>
              <h1 className="text-2xl font-semibold sm:text-3xl">AI Guide Content</h1>
              <p className="mt-2 text-sm leading-6 text-slate-300">
                Build the evidence, Story Map, and Guide Shadow that the app retrieves when it creates
                personalized AI stops.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={loadPackageFromCloud}
                disabled={isLoadingCloud}
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/15 disabled:opacity-50"
              >
                {isLoadingCloud
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <CloudDownload className="h-4 w-4" />}
                Load saved
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                onChange={importPackage}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/15"
              >
                <FileUp className="h-4 w-4" />
                Import JSON
              </button>
              {packageData && (
                <button
                  type="button"
                  onClick={savePackageToCloud}
                  disabled={isSavingCloud}
                  className="inline-flex items-center gap-2 rounded-lg border border-emerald-300/40 bg-emerald-400/15 px-4 py-2 text-sm font-medium text-emerald-100 hover:bg-emerald-400/25 disabled:opacity-50"
                >
                  {isSavingCloud
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <CloudUpload className="h-4 w-4" />}
                  {isSavingCloud ? 'Saving...' : 'Save JSON'}
                </button>
              )}
              <button
                type="button"
                onClick={createStarter}
                className="inline-flex items-center gap-2 rounded-lg bg-sky-400 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-sky-300"
              >
                <Plus className="h-4 w-4" />
                {packageData ? 'Start again' : 'Create from guide'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {notice && (
        <div className="mb-5 flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{notice}</span>
          <button
            type="button"
            onClick={() => setNotice('')}
            className="ml-auto text-sky-500 hover:text-sky-800"
          >
            Close
          </button>
        </div>
      )}

      {!packageData ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-16 text-center">
          <FileJson className="mx-auto h-10 w-10 text-slate-300" />
          <h2 className="mt-4 text-lg font-semibold text-slate-900">No AI content package loaded</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-500">
            Create a starter from this guide to prefill stops and recorded scripts, or import an
            existing Content Treasure such as the Cascais pilot.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <button
              type="button"
              onClick={createStarter}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              <Plus className="h-4 w-4" />
              Create from guide
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <FileUp className="h-4 w-4" />
              Import JSON
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[14rem_minmax(0,1fr)]">
          <aside>
            <div className="sticky top-4 rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
              {SECTIONS.map((section) => {
                const Icon = section.icon;
                const active = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setActiveSection(section.id)}
                    className={`mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition last:mb-0 ${
                      active
                        ? 'bg-slate-900 text-white'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{section.label}</span>
                    {section.countKey && (
                      <span className={`text-xs ${active ? 'text-slate-300' : 'text-slate-400'}`}>
                        {packageData[section.countKey].length}
                      </span>
                    )}
                    {section.id === 'validation' && validation.errors > 0 && (
                      <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] text-white">
                        {validation.errors}
                      </span>
                    )}
                  </button>
                );
              })}
              <div className="mt-3 border-t border-slate-100 px-3 pt-3">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Save className="h-3.5 w-3.5" />
                  Local draft autosaved
                </div>
              </div>
            </div>
          </aside>
          <main className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-sm sm:p-6">
            {renderSection()}
          </main>
        </div>
      )}
    </div>
  );
}
